/**
 * EVO — mahsulot katalogi ma'lumot qatlami.
 *
 * ============================================================================
 * PUL VA MIQDOR — `string`, `number` EMAS
 * ============================================================================
 * `avgCost`, `weightKg`, `minStock` va boshqalar Postgres `numeric` dan matn
 * ko'rinishida keladi (`"14977.4100"`). Bu yerda ular AYNAN shunday qoladi va
 * `format.ts` orqali ko'rsatiladi — `parseFloat` taqiqlangan (format.ts izohi).
 *
 * ============================================================================
 * `avgCost` — IXTIYORIY MAYDON, VA BU ATAYLAB
 * ============================================================================
 * Server `product.cost.view` ruxsati bo'lmasa `avgCost` ni javobga UMUMAN
 * qo'shmaydi (products.service.ts `projection()`), `null` ham qilmaydi.
 * Shuning uchun tipda ham `avgCost?: string` — ya'ni TypeScript uni
 * tekshirmasdan ishlatishga yo'l qo'ymaydi. `avgCost: string | null` deb
 * yozsak, «ruxsat yo'q» va «tannarx hali hisoblanmagan» holatlari
 * bir-biridan farq qilmay qolardi.
 */
import { api, ApiError, isRecord } from './api';
import { parseListSearch, type ListSearch } from './list-search';

/** `common/list-query.ts` dagi `Page<T>` bilan bir xil shakl. */
export interface Page<T> {
  readonly items: readonly T[];
  readonly page: number;
  readonly pageSize: number;
  readonly total: number;
  readonly totalPages: number;
}

export const PRODUCT_ALIAS_TYPES = ['legacy', 'adines', '1c', 'supplier', 'custom'] as const;
export type ProductAliasType = (typeof PRODUCT_ALIAS_TYPES)[number];

export interface ProductAlias {
  readonly id: number;
  readonly alias: string;
  readonly aliasType: string;
  readonly sourceRef: string | null;
}

export interface Product {
  readonly id: number;
  readonly sku: string;
  /** `ctx.locale` bo'yicha server hal qilgan nom — ro'yxatda shu ko'rsatiladi. */
  readonly name: string;
  /** Xom nomlar tahrirlash formasi uchun: tarjimalar sekin-asta yo'qolmasligi kerak. */
  readonly nameUz: string;
  readonly nameRu: string | null;
  readonly nameEn: string | null;
  readonly categoryId: number;
  readonly brandId: number | null;
  readonly uomId: number;
  readonly description: string | null;
  readonly weightKg: string | null;
  readonly volumeM3: string | null;
  readonly isSellable: boolean;
  readonly isPurchasable: boolean;
  readonly minStock: string | null;
  readonly maxStock: string | null;
  readonly reorderPoint: string | null;
  /**
   * QQS stavkasi foizda (`"12.00"`). HECH QACHON `null` emas — bazada
   * `NOT NULL DEFAULT 0`.
   *
   * ┌──────────────────────────────────────────────────────────────────────┐
   * │ `"0.00"` — «SOLIQSIZ», «to'ldirilmagan» EMAS                          │
   * │                                                                      │
   * │ 0% li tovar (eksport, imtiyozli ro'yxat) haqiqiy va keng tarqalgan,   │
   * │ shuning uchun ekranda nol stavka «bo'sh» ko'rinishida (`—`)           │
   * │ chizilmaydi: u to'liq huquqli qiymat.                                 │
   * │                                                                      │
   * │ Bu qiymat hujjatga NUSXA bo'lib ko'chadi. Kartochkani tahrirlash      │
   * │ o'tgan hisob-fakturalarga TA'SIR QILMAYDI.                            │
   * └──────────────────────────────────────────────────────────────────────┘
   */
  readonly vatRate: string;
  readonly isActive: boolean;
  readonly archivedAt: string | null;
  readonly version: number;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly categoryName: string | null;
  readonly brandName: string | null;
  readonly uomCode: string | null;

  /** FAQAT `product.cost.view` ruxsati bilan keladi — fayl boshidagi izohga qarang. */
  readonly avgCost?: string;

  /**
   * Erkin qoldiq (`on_hand − reserved`) — ro'yxatda ham, kartochkada ham keladi.
   *
   * `avgCost` dan FARQLI o'laroq bu maydon ruxsat bilan cheklanmaydi va
   * `PROTECTED_FIELDS` ro'yxatida yo'q: miqdor tannarx emas. Jonli
   * tekshirilgan — `sotuvchi@evo.uz` javobida `availableStock` BOR,
   * `avgCost` esa YO'Q.
   *
   * Ko'lam esa baribir qo'llanadi: `availableStockExpr()` faqat foydalanuvchiga
   * ruxsat etilgan omborlarni qo'shadi, ya'ni omborchi o'z omboridagi
   * qoldiqni ko'radi. Shuning uchun raqam «bu tovar bormi?» degan savolga
   * SO'RAYOTGAN ODAM uchun javob beradi.
   *
   * Ixtiyoriy (`?`) qolgani sabab: `Product` shakli ro'yxat, kartochka va
   * import oldindan ko'rish javoblari uchun umumiy, va maydon kelmagan
   * javobni tip darajasida hisobga olish uni `—` deb chizishdan ko'ra
   * xavfsizroq.
   */
  readonly availableStock?: string;
}

export interface ProductDetail extends Product {
  readonly aliases: readonly ProductAlias[];
}

/** Autocomplete natijasi — `GET /api/products/search`. */
export interface ProductSearchItem {
  readonly id: number;
  readonly sku: string;
  readonly name: string;
  readonly uomCode: string | null;
  readonly availableStock: string;
}

/** Ma'lumotnoma yozuvi: brend, o'lchov birligi (`ReferenceResponse`). */
export interface ReferenceItem {
  readonly id: number;
  readonly code: string;
  readonly name: string;
  readonly isActive: boolean;
}

/** Kategoriya daraxti — `GET /api/categories` tekis emas, ichma-ich qaytaradi. */
export interface CategoryNode {
  readonly id: number;
  readonly parentId: number | null;
  readonly code: string;
  readonly name: string;
  readonly path: string;
  readonly level: number;
  readonly isActive: boolean;
  readonly children: readonly CategoryNode[];
}

// ---------------------------------------------------------------------------
// Ro'yxat parametrlari
// ---------------------------------------------------------------------------

export type SortDir = 'asc' | 'desc';

export interface ProductListParams {
  readonly page: number;
  readonly pageSize: number;
  readonly sortBy?: string | undefined;
  readonly sortDir: SortDir;
  readonly q?: string | undefined;
  /**
   * Kategoriya va brend filtri.
   *
   * ┌──────────────────────────────────────────────────────────────────────┐
   * │ SERVER BULARNI ALLAQACHON BILARDI — EKRAN SO'RAMASDI                 │
   * │                                                                      │
   * │ `ProductListQuerySchema` da `categoryId` va `brandId` 0000 dan beri  │
   * │ bor va `products.service.ts` → `listWhere()` ularni qo'llaydi. Web   │
   * │ esa faqat `q` yuborardi, ya'ni 40 000 SKU li katalogda «Filips       │
   * │ lampalari» ni ko'rishning yagona yo'li nom bo'yicha qidirish edi —   │
   * │ va u brend nomi mahsulot nomida yozilmagan qatorlarni TOPMASDI.      │
   * └──────────────────────────────────────────────────────────────────────┘
   */
  readonly categoryId?: number | undefined;
  readonly brandId?: number | undefined;
}

/**
 * Mahsulotlar ro'yxatining URL holati.
 *
 * `ListSearch` (sahifa, tartib, qidiruv) ustiga katalogga XOS ikkita filtr
 * qo'shiladi. Alohida tur kerak, chunki `ListSearch` — barcha ro'yxatlar
 * uchun umumiy shakl va unga bitta ekranning maydonini qo'shish qolgan
 * o'ntasini ham o'sha maydon bilan yuklardi.
 */
export interface ProductSearch extends ListSearch {
  readonly categoryId?: number;
  readonly brandId?: number;
}

/**
 * URL dagi filtrlarni tozalaydi.
 *
 * Manzil ISHONCHSIZ kirish: `?categoryId=abc` yoki `?brandId=-5` serverga
 * borsa 400 qaytarardi va jadval «xatolik» holatiga tushardi — sabab esa
 * ekranda ko'rinmasdi (`procurement-search.ts` dagi bir xil qoida).
 */
export function parseProductSearch(search: Record<string, unknown>): ProductSearch {
  const positive = (value: unknown): number | undefined => {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
  };

  const categoryId = positive(search['categoryId']);
  const brandId = positive(search['brandId']);

  return {
    ...parseListSearch(search),
    ...(categoryId !== undefined ? { categoryId } : {}),
    ...(brandId !== undefined ? { brandId } : {}),
  };
}

/**
 * Serverda saralashga ruxsat etilgan maydonlar (`PRODUCT_SORTABLE`).
 *
 * Bu ro'yxatdan tashqaridagi ustun sarlavhasi BOSILADIGAN qilinmaydi: server
 * `Saralash maydoni noto'g'ri` deb 400 qaytaradi va foydalanuvchi ustun
 * bosilgani bilan jadval buzilganini ko'radi.
 */
export const PRODUCT_SORTABLE_FIELDS = ['sku', 'nameUz', 'avgCost', 'createdAt', 'updatedAt'] as const;

// ---------------------------------------------------------------------------
// Query kalitlari
// ---------------------------------------------------------------------------

export const productKeys = {
  all: ['products'] as const,
  list: (params: ProductListParams) => ['products', 'list', params] as const,
  detail: (id: number) => ['products', 'detail', id] as const,
  search: (q: string) => ['products', 'search', q] as const,
  categories: ['products', 'categories'] as const,
  brands: ['products', 'brands'] as const,
  uoms: ['products', 'uoms'] as const,
};

// ---------------------------------------------------------------------------
// O'qish
// ---------------------------------------------------------------------------

export function fetchProducts(params: ProductListParams, signal?: AbortSignal): Promise<Page<Product>> {
  return api.get<Page<Product>>('/products', {
    query: {
      page: params.page,
      pageSize: params.pageSize,
      sortBy: params.sortBy,
      sortDir: params.sortDir,
      q: params.q,
      categoryId: params.categoryId,
      brandId: params.brandId,
    },
    ...(signal ? { signal } : {}),
  });
}

/**
 * Jonli qidiruv.
 *
 * Server natijalarni AYNAN MOSLIK bo'yicha tartiblaydi (to'liq SKU → to'liq
 * alias → prefiks → qolganlar), shuning uchun bu yerda qayta saralash
 * QILINMAYDI: omborchi to'liq kodni terganda birinchi qator aynan o'shanikidir.
 */
export function searchProducts(q: string, signal?: AbortSignal): Promise<readonly ProductSearchItem[]> {
  return api.get<readonly ProductSearchItem[]>('/products/search', {
    query: { q, limit: 10 },
    ...(signal ? { signal } : {}),
  });
}

export function fetchProduct(id: number): Promise<ProductDetail> {
  return api.get<ProductDetail>(`/products/${id}`);
}

export function fetchCategories(): Promise<readonly CategoryNode[]> {
  return api.get<readonly CategoryNode[]>('/categories');
}

export function fetchBrands(): Promise<Page<ReferenceItem>> {
  // Ma'lumotnomalar kichik va tanlash ro'yxatiga to'liq tushishi kerak —
  // sahifalash bu yerda foyda bermaydi, faqat «brendim ro'yxatda yo'q» deydi.
  return api.get<Page<ReferenceItem>>('/brands', { query: { pageSize: 200, sortBy: 'name' } });
}

export function fetchUoms(): Promise<Page<ReferenceItem>> {
  return api.get<Page<ReferenceItem>>('/uoms', { query: { pageSize: 200, sortBy: 'name' } });
}

/**
 * Kategoriya daraxtini tanlash ro'yxati uchun tekislaydi.
 *
 * Ichma-ich `<optgroup>` HTML da mavjud emas, shuning uchun ierarxiya
 * bo'shliq bilan ko'rsatiladi. Ierarxiyani umuman tashlab yuborish mumkin
 * emas: «Panel» degan ikkita kategoriya turli shoxlarda bo'lishi mumkin va
 * ular ajratib bo'lmas holga keladi.
 */
export function flattenCategories(
  nodes: readonly CategoryNode[],
  depth = 0,
): Array<{ readonly id: number; readonly label: string }> {
  return nodes.flatMap((node) => [
    { id: node.id, label: `${'  '.repeat(depth)}${node.name}` },
    ...flattenCategories(node.children, depth + 1),
  ]);
}

// ---------------------------------------------------------------------------
// Yozish
// ---------------------------------------------------------------------------

export interface ProductAliasInput {
  readonly alias: string;
  readonly aliasType: ProductAliasType;
  readonly sourceRef?: string | null;
}

export interface CreateProductInput {
  readonly sku: string;
  readonly nameUz: string;
  readonly nameRu?: string | null;
  readonly nameEn?: string | null;
  readonly categoryId: number;
  readonly brandId?: number | null;
  readonly uomId: number;
  readonly description?: string | null;
  readonly weightKg?: string | null;
  readonly volumeM3?: string | null;
  readonly isSellable?: boolean;
  readonly isPurchasable?: boolean;
  readonly minStock?: string | null;
  readonly maxStock?: string | null;
  readonly reorderPoint?: string | null;
  /**
   * `null` EMAS, `undefined`: server sxemasi (`CreateProductSchema`)
   * `null` ni QABUL QILMAYDI va `.strict()` uni 400 bilan rad etadi.
   *
   * Sabab: «stavka yo'q» degan holat mavjud emas — bo'sh katak «tozalash»
   * emas, «tegilmadi» degani. Yaratishda berilmasa server tenant
   * standartini (`settings.vat_default_rate`) qo'yadi.
   */
  readonly vatRate?: string;
  readonly aliases?: readonly ProductAliasInput[];
}

/** Tahrirlash — `aliases` yo'q (alohida oqim), `version` bor (optimistik qulf). */
export type UpdateProductInput = Omit<CreateProductInput, 'aliases'> & { readonly version: number };

/**
 * Mahsulot yaratadi.
 *
 * `force = true` — «yaqin dublikat» (422) ogohlantirishini bilib turib chetlab
 * o'tish. SKU to'qnashuvi (409) bunda ham qolaveradi: u ogohlantirish emas,
 * qat'iy cheklov (`ux_products_sku`).
 */
export function createProduct(input: CreateProductInput, force = false): Promise<ProductDetail> {
  return api.post<ProductDetail>('/products', input, force ? { query: { force: 'true' } } : {});
}

export function updateProduct(id: number, input: UpdateProductInput): Promise<ProductDetail> {
  return api.patch<ProductDetail>(`/products/${id}`, input);
}

// ---------------------------------------------------------------------------
// Xato javoblarini o'qish
// ---------------------------------------------------------------------------

export interface DuplicateCandidate {
  readonly id: number;
  readonly sku: string;
  readonly name: string;
}

const NEAR_DUPLICATE_CODE = 'PRODUCT_NEAR_DUPLICATE';
const SKU_EXISTS_CODE = 'PRODUCT_SKU_EXISTS';

/**
 * 422 «yaqin dublikat» javobidan nomzodlarni ajratadi.
 *
 * Xato JIM YUTILMAYDI va oddiy qizil matnga aylantirilmaydi (GAP_ANALYSIS §A2):
 * operator aynan qaysi mahsulot bilan to'qnashayotganini ko'rishi kerak, aks
 * holda u shunchaki SKU ni ozgina o'zgartirib qayta yuboradi — va dublikat
 * baribir tug'iladi.
 *
 * `null` — bu boshqa xato, chaqiruvchi uni odatdagidek ko'rsatadi.
 */
export function nearDuplicateCandidates(error: unknown): readonly DuplicateCandidate[] | null {
  if (!(error instanceof ApiError) || error.status !== 422) return null;
  if (!isRecord(error.body) || error.body.code !== NEAR_DUPLICATE_CODE) return null;

  const raw = error.body.candidates;
  if (!Array.isArray(raw)) return [];

  return raw.filter(
    (item): item is DuplicateCandidate =>
      isRecord(item) &&
      typeof item.id === 'number' &&
      typeof item.sku === 'string' &&
      typeof item.name === 'string',
  );
}

/** 409 SKU to'qnashuvi — maydon darajasidagi xato sifatida ko'rsatiladi. */
export function isSkuConflict(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409 && error.code === SKU_EXISTS_CODE;
}

/**
 * 409 optimistik qulf.
 *
 * Server bu holatda `code` bermaydi — faqat 409 va matn (products.service.ts).
 * Shuning uchun ajratish TESKARI yo'l bilan: 409, lekin SKU to'qnashuvi emas.
 * Yangi 409 kodi qo'shilsa, u ham shu shoxga tushadi — bu ataylab xavfsiz
 * tomon: «kimdir o'zgartirdi, qayta yuklang» xabari eng yomon holatda ortiqcha
 * ehtiyotkorlik bo'ladi, ma'lumot yo'qolishiga esa olib kelmaydi.
 */
export function isVersionConflict(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409 && !isSkuConflict(error);
}
